/* eslint-disable react-hooks/exhaustive-deps */
import { withRouter } from "../_shared/withRouter";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Header from "../template/_common/header";
import SidebarComponent from "../template/admin/sidebarComponent";
import SidebarArrow from "../template/_common/sidebarArrow";
import { globalAlert, globalLoader } from "../actions/commonActions";
import { logOut } from "../_shared/commonFunction";
import { lmsDashboardApi } from "./_lmsActions";

const asArray = (value) => (Array.isArray(value) ? value : []);

const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

const clampPercent = (value) => Math.min(Math.max(Math.round(num(value)), 0), 100);

const percent = (value) => `${clampPercent(value)}%`;

const fmtDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// const empty = (t) => (
//   <div className="lms-dashboard-empty">{t("common.noDataAvailable")}</div>
// );

const DASHBOARD_DAY_OPTIONS = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 180, label: "Last 6 months" },
  { value: 365, label: "Last 12 months" },
];

const LmsOverview = (props) => {
  const { t } = useTranslation();
  const [navbarToggle, setNavbarToggle] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [allowView, setAllowView] = useState(false);
  const [selectedDays, setSelectedDays] = useState(30);

  useEffect(() => {
    callBasicApi();
  }, []);

  const callBasicApi = async (days = selectedDays) => {
    try {
      globalLoader(true);
      const res = await lmsDashboardApi({ days });
      if (res?.data?.status === 200) {
        setDashboard(res?.data?.data || {});
        setAllowView(true);
      } else {
        globalAlert("error", res?.data?.message);
      }
      globalLoader(false);
    } catch (error) {
      globalAlert("error", error?.response?.data?.message);
      if (error?.response?.data?.status === 401) {
        logOut(props?.history, "/admin/login");
      }
      globalLoader(false);
    }
  };

  const handlePeriodChange = (event) => {
    const days = Number(event.target.value);
    setSelectedDays(days);
    callBasicApi(days);
  };

  const rawSummary = dashboard?.summary || {};
  const legacyStats = dashboard?.stats || {};
  const recentRegistrations = asArray(
    dashboard?.registrations?.recent?.length
      ? dashboard.registrations.recent
      : dashboard?.recentActivity?.registrations?.length
        ? dashboard.recentActivity.registrations
        : dashboard?.recentRegistrations
  );
  const pendingRegistrationRows = asArray(
    dashboard?.attention?.pendingRegistrations?.length
      ? dashboard.attention.pendingRegistrations
      : dashboard?.registrations?.pending
  );
  const summary = {
    courses: {
      total: firstDefined(rawSummary?.courses?.total, legacyStats?.totalCourses, 0),
      published: firstDefined(rawSummary?.courses?.published, legacyStats?.publishedCourses, 0),
      publishRate: firstDefined(rawSummary?.courses?.publishRate, 0),
    },
    learners: {
      total: firstDefined(rawSummary?.learners?.total, legacyStats?.registeredLearners, legacyStats?.totalLearners, 0),
      active: firstDefined(rawSummary?.learners?.active, legacyStats?.activeLearners, 0),
      activationRate: firstDefined(rawSummary?.learners?.activationRate, 0),
    },
    enrolments: {
      total: firstDefined(rawSummary?.enrolments?.total, legacyStats?.enrolments, 0),
      notStarted: firstDefined(rawSummary?.enrolments?.notStarted, 0),
      inProgress: firstDefined(rawSummary?.enrolments?.inProgress, 0),
      completed: firstDefined(rawSummary?.enrolments?.completed, legacyStats?.completions, 0),
      completionRate: firstDefined(rawSummary?.enrolments?.completionRate, legacyStats?.completionRate, 0),
    },
    registrations: {
      total: firstDefined(rawSummary?.registrations?.total, recentRegistrations.length, 0),
      pending: firstDefined(rawSummary?.registrations?.pending, legacyStats?.pendingApprovals, pendingRegistrationRows.length, 0),
    },
    quizzes: {
      passRate: firstDefined(rawSummary?.quizzes?.passRate, legacyStats?.quizPassRate, 0),
    },
  };
  const queue = {
    pendingRegistrations: pendingRegistrationRows,
    stalledLearners: asArray(dashboard?.attention?.stalledLearners),
    lowCompletionCourses: asArray(dashboard?.attention?.lowCompletionCourses),
  };
  const recentActivity = dashboard?.recentActivity || {};
  const funnel = [
    {
      label: "Active Staff Accounts",
      value: summary?.learners?.active ?? 0,
      hint: `${summary?.learners?.total ?? 0} Total Accounts`,
      icon: "fas fa-user-graduate",
      tone: "teal",
      iconBg: "#20C4D8",
    },
    {
      label: "Course Enrollments",
      value: summary?.enrolments?.total ?? 0,
      hint: `${summary?.enrolments?.notStarted ?? 0} Not Started`,
      icon: "fas fa-clipboard-list",
      tone: "blue",
      iconBg: "#5B8DEF",
    },
    {
      label: "Courses Completed",
      value: summary?.enrolments?.completed ?? 0,
      hint: `${summary?.enrolments?.inProgress ?? 0} Still In Progress`,
      icon: "fas fa-check-circle",
      tone: "green",
      iconBg: "#43C26B",
    },
  ];
  const generatedLabel = dashboard?.meta?.generatedAt
    ? `Last generated ${fmtDateTime(dashboard.meta.generatedAt)}`
    : "Dashboard data";

  return (
    <>
      <Helmet>
        <title>{t("admin.pageTitle.lmsOverview")} - eC-Card</title>
        <meta name="viewport" content="width=1200, initial-scale=1" />
      </Helmet>
      {allowView && (
        <div className="admin-portal">
          <Header roleType="admin" />
          <div className={`${navbarToggle ? "active" : ""} wrapper`}>
            <SidebarComponent
              navbarToggle={navbarToggle}
              roleType="admin"
              menu="lms"
              subMenu="lms-overview"
            />
            <div className="main">
              <div className="main-wrapper">
                <div className="lms-dashboard-header">
                  <div>
                    <p className="admin-banner mb-1">{t("admin.lms.overview.title")}</p>
                    <span className="lms-dashboard-meta">
                      {generatedLabel} - Last {dashboard?.meta?.periodDays || selectedDays} days
                    </span>
                  </div>
                  <div className="lms-dashboard-actions">
                    <label className="lms-period-control">
                      <span>Period</span>
                      <select value={selectedDays} onChange={handlePeriodChange}>
                        {DASHBOARD_DAY_OPTIONS.map((option) => (
                          <option value={option.value} key={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button className="btn secondary-btn lms-refresh-btn" onClick={() => callBasicApi()}>
                      <i className="fas fa-sync-alt"></i>
                      <span>Refresh</span>
                    </button>
                  </div>
                </div>
                <SidebarArrow handleSidebarToggle={setNavbarToggle} />

                <div className="container-fluid pt-3 lms-dashboard">
                  <div className="row">
                    <div className="col-xl-4 mb-3">
                      <DashboardPanel title="Needs Action" actionLabel="Open Requests" onAction={() => props?.history?.push("/admin/lms/learners?status=pending-approval")}>
                        <QueueItem
                          label="Approve learner requests"
                          value={queue?.pendingRegistrations?.length || 0}
                          tone="amber"
                          detail="Waiting for an admin decision"
                        />
                        <QueueItem
                          label="Re-engage learners"
                          value={queue?.stalledLearners?.length || 0}
                          tone="red"
                          detail="Started learning but went quiet"
                        />
                        <QueueItem
                          label="Review course drop-off"
                          value={queue?.lowCompletionCourses?.length || 0}
                          tone="blue"
                          detail="Courses with low completion"
                        />
                      </DashboardPanel>
                    </div>

                    <div className="col-xl-8 mb-3">
                      <DashboardPanel title="Learner Journey">
                        <div className="row g-4">
                          {funnel.map((item) => (
                            <div className="col-lg-4 col-md-6" key={item.label}>
                              <div className={`card h-100 border-0 shadow-sm tone-${item.tone}`}>
                                <div className="card-body">

                                  <div className="d-flex align-items-start">
                                    <div className="me-3">
                                      <span
                                        className="lms-funnel-icon"
                                        style={{ backgroundColor: item.iconBg }}
                                      >
                                        <i className={item.icon}></i>
                                      </span>
                                    </div>

                                    <div className="flex-grow-1">
                                      <h6 className="mb-2">{item.label}</h6>
                                      <h2 className="fw-bold mb-0">{item.value}</h2>
                                    </div>
                                  </div>

                                  <hr />

                                  <div className="d-flex align-items-center">
                                    <span
                                      className="status-dot me-2"
                                      style={{ backgroundColor: item.iconBg }}
                                    ></span>
                                    <small>{item.hint}</small>
                                  </div>

                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </DashboardPanel>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-xl-5 mb-3">
                      <DashboardPanel title="Courses To Review" actionLabel="View Courses" onAction={() => props?.history?.push("/admin/lms/courses?status=all")}>
                        <AttentionCourseList
                          rows={queue?.lowCompletionCourses || []}
                          t={t}
                          onCourseClick={(course) => props?.history?.push(`/admin/lms/course/detail/${course.slug || course.courseId || course._id}`)}
                        />
                      </DashboardPanel>
                    </div>

                    <div className="col-xl-7 mb-3">
                      <DashboardPanel title="Recent Activity" actionLabel="All Registrations" onAction={() => props?.history?.push("/admin/lms/learners?status=all")}>
                        <div className="lms-recent-split">
                          <ActivityList
                            rows={recentRegistrations.slice(0, 4)}
                            primary={(row) => row.name}
                            secondary={(row) => row.organisation}
                            aside={(row) => <StatusBadge status={row.status} t={t} />}
                            dateKey="createdAt"
                            t={t}
                          />
                          <div className="lms-recent-completions">
                            <h4>Recent Completions</h4>
                            <ActivityList
                              rows={asArray(recentActivity?.completions).slice(0, 4)}
                              primary={(row) => row.learnerName}
                              secondary={(row) => row.courseTitle}
                              aside={(row) => fmtDate(row.completedAt)}
                              dateKey="completedAt"
                              t={t}
                            />
                          </div>
                        </div>
                      </DashboardPanel>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const DashboardPanel = ({ title, actionLabel, onAction, children }) => (
  <section className="lms-dashboard-panel">
    <div className="lms-dashboard-panel-head">
      <h3>{title}</h3>
      {actionLabel ? (
        <button type="button" className="btn secondary-btn modal-btn" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
    {children}
  </section>
);

const QueueItem = ({ label, value, tone, detail }) => (
  <div className={`lms-queue-item tone-${tone}`}>
    <div>
      <span>{label}</span>
      <small>{detail}</small>
    </div>
    <strong>{value}</strong>
  </div>
);

const AttentionCourseList = ({ rows, t, onCourseClick }) => (
  <div className="lms-attention-course-list">
    {rows?.length ? (
      rows.map((course) => (
        <button
          type="button"
          className="lms-attention-course"
          key={course.courseId || course._id || course.title}
          onClick={() => (course.courseId || course._id) && onCourseClick(course)}
        >
          <div>
            <strong>{course.title || "Untitled course"}</strong>
            <span>{course.enrolments || 0} enrolments - {course.completions || 0} completions</span>
          </div>
          <em>{percent(course.completionRate)}</em>
        </button>
      ))
    ) : (
      <div className="card border-0 shadow-sm">
        <div className="card-body text-center py-5">

          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light mb-3"
            style={{ width: "80px", height: "80px" }}
          >
            <i
              className="fas fa-book-open text-secondary"
              style={{ fontSize: "32px" }}
            ></i>
          </div>

          <h5 className="fw-semibold mb-2">No Courses Found</h5>

          <p className="text-muted mb-4">
            There are currently no courses available.
          </p>



        </div>
      </div>
    )}
  </div>
);

const ActivityList = ({ rows, primary, secondary, aside, dateKey, t }) => (
  <div className="lms-activity-list">
    {rows?.length ? (
      rows.map((row) => (
        <div className="lms-activity-row " key={row._id}>
          <div>
            <strong>{primary(row) || "-"}</strong>
            <span>{secondary(row) || "-"}</span>
            {dateKey ? <small>{fmtDateTime(row[dateKey])}</small> : null}
          </div>
          <div className="lms-activity-aside">{aside(row)}</div>
        </div>
      ))
    ) : (
      <div className="text-center py-5">
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light mb-3"
          style={{ width: "80px", height: "80px" }}
        >
          <i
            className="fas fa-chart-line text-secondary"
            style={{ fontSize: "32px" }}
          ></i>
        </div>

        <h5 className="fw-semibold mb-2">No Activity Found</h5>

        <p className="text-muted mb-0">
          There is no learner activity to display at the moment.
        </p>
      </div>
    )}
  </div>
);

export const StatusBadge = ({ status, t }) => {
  const s = (status || "").toLowerCase();
  let cls = "badge-gray";
  if (["approved", "active", "published", "completed", "pass"].includes(s))
    cls = "badge-green";
  else if (["pending", "draft", "in progress", "inprogress", "not_started"].includes(s))
    cls = "badge-amber";
  else if (["rejected", "inactive", "suspended", "fail", "archived"].includes(s))
    cls = "badge-red";
  const label = status
    ? t(`admin.lms.status.${s.replace(/[\s_]+/g, "")}`, status)
    : "-";
  return <span className={`lms-badge ${cls}`}>{label}</span>;
};

export default withRouter(LmsOverview);
